import { Controller } from '@hotwired/stimulus'

// Whether one region of a form differs from how it arrived.
//
// `form-change` answers that for a whole form, which it can do with `FormData`.
// A region has no form of its own, so this walks the fields it contains and
// compares them to the baseline it took on connect. Use it wherever part of a
// form has to answer for itself rather than for everything around it.
export default class PartialFormChangesController extends Controller<HTMLElement> {
  // How a region says it is holding something. Stimulus writes the value out as
  // this attribute, so the DOM answers without anyone having to ask the
  // controller -- a CSS rule or a stream target selector can read it directly.
  static MODIFIED_SELECTOR = '[data-partial-form-changes-modified-value="true"]'

  // Content the server says the region arrived holding. A region can be
  // rendered with changes already in place -- a staged upload, or a message
  // typed before a refused save re-rendered the panel around it -- and that
  // belongs in the answer rather than in the baseline it is measured against.
  // Written from Ruby by `PartialFormChanges.unsaved_data`.
  static UNSAVED_SELECTOR = '[data-partial-form-changes-unsaved]'

  declare modifiedValue: boolean

  static values = { modified: Boolean }

  #baseline = ''

  // Some fields change without an `input` event, like a removed upload or a
  // restored attachment, so DOM changes trigger a check too.
  #observer = new MutationObserver(() => this.#check())
  #frame = 0
  #touched = false

  connect() {
    this.#touched = false
    this.#baseline = this.#cleanSnapshot()

    // Retaken a frame later so controllers that rewrite fields on connect are
    // done first, unless the user has already changed something.
    this.#frame = requestAnimationFrame(() => {
      if (!this.#touched) this.#baseline = this.#cleanSnapshot()
      this.#check()

      this.#observer.observe(this.element, {
        attributeFilter: ['disabled', 'name', 'value', 'checked'],
        attributes: true,
        childList: true,
        subtree: true,
      })
    })

    this.element.addEventListener('input', this.#input)
  }

  disconnect() {
    cancelAnimationFrame(this.#frame)
    this.#observer.disconnect()
    this.element.removeEventListener('input', this.#input)
  }

  modifiedValueChanged() {
    this.dispatch('changed')
  }

  #input = () => {
    this.#touched = true
    this.#check()
  }

  #check = () => {
    this.modifiedValue = this.#snapshot() !== this.#baseline
  }

  // What the region holds now.
  #snapshot() {
    return this.#stateOf(this.#fields())
  }

  // What the region would hold with nothing unsaved in it, which is the state a
  // change is measured against.
  #cleanSnapshot() {
    const selector = PartialFormChangesController.UNSAVED_SELECTOR

    return this.#stateOf(this.#fields().filter(field => !field.closest(selector)))
  }

  // Named, enabled fields only. File inputs are left out: what they stage shows
  // up as whatever hidden fields their own controller writes.
  #fields() {
    return Array.from(this.element.querySelectorAll<FormField>('[name]')).filter(
      field => !field.disabled && field.type !== 'file',
    )
  }

  #stateOf(fields: FormField[]) {
    return fields
      .map(field => `${field.name}=${this.#valueOf(field)}`)
      .sort()
      .join('&')
  }

  // A tick is a change of state, not of value, so checkboxes and radios report
  // their value only while they are checked. A multiple select reports all of
  // what is picked rather than the first of it.
  #valueOf(field: FormField) {
    if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) {
      return field.checked ? field.value : ''
    }

    if (field instanceof HTMLSelectElement && field.multiple) {
      return Array.from(field.selectedOptions)
        .map(option => option.value)
        .join(',')
    }

    return field.value
  }
}

type FormField = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
